import { inputs as i } from "@/components/input";
import { Route as FootPhotos } from "@/routes/_app/diaries/$diaryId/new/tracker/$trackerId/entry/$entryId/page/$pageIndex/foot-photos/start";
import { services as s } from "@/services";
import type { InputT } from "@/types/database/forms";
import { Navigate, useParams } from "@tanstack/react-router";
import React, { useState } from "react";

import { formComponents as fc } from "@/components/form";
import YesNo from "@/components/form/YesNo";
import { ui } from "@/components/ui";
import { useEntryValuesStore } from "../hooks/useEntryValuesStore";

interface Props {
  is_preview?: boolean;
  input: InputT;
  form: Record<string, any>; // Form object from react-hook-form
}

const InputSwitch = ({ input, is_preview, form }: Props) => {
  const { entryId, entryValueId: paramEntryValueId } = useParams({
    strict: false,
  }) as {
    entryId: string;
    entryValueId?: string; // Make optional as it might not exist yet
  };
  // Get the store functions/selectors
  const getEntryValue = useEntryValuesStore(
    (state) => state.getEntryValueByInputId,
  );
  const upsertEntryValue = useEntryValuesStore(
    (state) => state.upsertEntryValue,
  );
  // Use state to hold the generated ID if needed, ensuring it's generated only once
  const [localEntryValueId] = useState(() => paramEntryValueId ?? s.uuidv4());
  // Get the current value for comparison if needed (though not used in the optimized effect)
  const entryValue = getEntryValue(input.input_id);

  React.useEffect(() => {
    // Check if the entry value already exists in the store for this specific input_id
    const existingEntryValue = getEntryValue(input.input_id);

    // Only upsert if it doesn't exist in the store yet
    if (!existingEntryValue) {
      upsertEntryValue({
        entry_id: entryId,
        input_id: input.input_id,
        entry_value_id: localEntryValueId, // Use the stable, potentially generated ID
        // Initialize other fields to null or default as appropriate
        number_value: null,
        text_value: null,
        text_array: null,
        json_value: null,
        object_id: null,
      });
    }
    // Depend only on stable identifiers necessary for initialization
  }, [
    entryId,
    input.input_id,
    localEntryValueId,
    upsertEntryValue,
    getEntryValue,
  ]);

  switch (input.input_key) {
    case "short-text":
      return (
        <fc.Input
          name={input.input_id}
          label={input.input_label ?? undefined}
          description={input.input_description ?? undefined}
        />
      );
    case "long-text":
      return (
        <fc.Textarea
          name={input.input_id}
          label={input.input_label ?? undefined}
          description={input.input_description ?? undefined}
        />
      );
    case "date-picker":
      return (
        <fc.Date
          name={input.input_id}
          label={input.input_label ?? undefined}
          description={input.input_description ?? undefined}
        />
      );
    case "date-time-picker":
      return (
        <fc.DateTime
          name={input.input_id}
          maxLength={input.max_length ?? undefined}
          label={input.input_label ?? undefined}
          description={input.input_description ?? undefined}
        />
      );
    case "number":
      return (
        <i.Number
          value={entryValue?.number_value?.toString() ?? ""}
          setValue={(val: string) => {
            upsertEntryValue({
              entry_id: entryId,
              input_id: input.input_id,
              entry_value_id: localEntryValueId,
              number_value: Number.parseFloat(val) || null,
            });
          }}
          label={input.input_label ?? undefined}
          description={input.input_description ?? undefined}
        />
      );
    case "yes-no":
      return (
        <ui.Card>
          <ui.CardContent className="pt-4">
            <YesNo
              name={input.input_id}
              label={input.input_label ?? ""}
            />
          </ui.CardContent>
        </ui.Card>
      );
    case "dropdown":
      return (
        <fc.Select
          options={input.options?.map((o) => ({
            label: o,
            value: o,
          })) ?? []}
          name={input.input_id}
          label={input.input_label ?? "Select"}
        />
      );
    case "duration":
      return <i.Duration form={form} input={input} />;
    case "email":
      return <fc.Email name={input.input_id} />;
    case "slider":
      return (
        <i.Slider
          setValue={(val: number) =>
            upsertEntryValue({
              entry_id: entryId,
              input_id: input.input_id,
              entry_value_id: localEntryValueId,
              number_value: val,
            })}
          value={entryValue?.number_value ?? 0}
          input={input}
          label={input.input_label ?? ""}
        />
      );
    case "single-choice":
      return <fc.SingleChoice input={input} />;
    case "scale-rating":
      return (
        <ui.Card>
          <ui.CardContent className="pt-4">
            <fc.Rating
              input={input}
              name={input.input_id}
              label={input.input_label ?? ""}
            />
          </ui.CardContent>
        </ui.Card>
      );
    case "star":
      return (
        <ui.Card>
          <ui.CardContent className="pt-4">
            <fc.Star
              input={input}
              name={input.input_id}
              label={input.input_label ?? ""}
            />
          </ui.CardContent>
        </ui.Card>
      );
    case "multiple-choice":
      return <i.MultipleChoice input={input} />;
    case "photo":
      return <i.Photo input={input} />;
    case "foot-photos":
      return is_preview ? <p>No Preview Available</p> : (
        Navigate({
          to: FootPhotos.to,
          params: {
            diaryId: useParams({ strict: false }).diaryId as string,
            trackerId: useParams({ strict: false })
              .trackerId as string,
            entryId: entryId,
            pageIndex: useParams({ strict: false })
              .pageIndex as string,
          },
        })
      );

    case "text-list":
      return (
        <ui.Card>
          <ui.CardContent className="pt-4">
            <fc.TextList
              name={input.input_id}
              label={input.input_label ?? ""}
              description={input.input_description ?? undefined}
            />
          </ui.CardContent>
        </ui.Card>
      );

    case "object-list":
      return (
        <ui.Card>
          <ui.CardContent className="pt-4">
            <fc.ObjectList
              name={input.input_id}
              label={input.input_label ?? ""}
              description={input.input_description ?? undefined}
            />
          </ui.CardContent>
        </ui.Card>
      );

    case "month-year":
      return (
        <fc.MonthYear
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );

    case "repeatable-group":
      if (input.input_list && input.input_list.length > 0) {
        return (
          <ui.Card>
            <ui.CardContent className="pt-4">
              <fc.RepeatableGroup
                name={input.input_id}
                label={input.input_label ?? ""}
                description={input.input_description ??
                  undefined}
                inputList={input.input_list}
              />
            </ui.CardContent>
          </ui.Card>
        );
      }
      return null;

    // Custom Inputs
    case "pain-slider":
      return (
        <i.ci.PainSlider
          name={input.input_id}
          label={input.input_label ?? undefined}
        />
      );
    case "glucose-slider":
      return (
        <i.ci.GlucoseSlider
          name={input.input_id}
          label={input.input_label ?? undefined}
        />
      );
    case "visa-a-8":
      return (
        <i.ci.VisaA8
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );
    case "visa-g-8":
      return (
        <i.ci.VisaG8
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );
    case "visa-p-8":
      return (
        <i.ci.VisaP8
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );
    case "visa-h-8":
      return (
        <i.ci.VisaH8
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );
    case "tried-treatment":
      return (
        <i.ci.TriedTreatment
          name={input.input_id}
          label={input.input_label ?? ""}
        />
      );
  }
};

export default InputSwitch;
